import { MagnifyingGlass, CheckCircle, X } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useCombobox } from 'downshift';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileContext } from '../../contexts/profileContext';
import { globalSearch } from '../../service';

const HighlightedText = ({ text = '', highlight = '' }) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }

  const regex = new RegExp(`(${highlight})`, 'gi');
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-100 text-gray-900 rounded-sm font-medium">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

export default function Search({ className }) {
  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const navigate = useNavigate();
  const [profile] = useContext(ProfileContext);
  const styleRadius = profile?.masterOrganization?.customConfigurations?.buttonRadius || '9999px';

  // Clear items on mount
  useEffect(() => {
    setItems([]);
  }, []);

  const handleSelectedItemChange = ({ selectedItem }) => {
    if (selectedItem) {
      const navigateURL =
        selectedItem.type === 'profile'
          ? `/${selectedItem.groupId}/profiles/${selectedItem._id}`
          : `/dashboard/${selectedItem._id}`;

      navigate(navigateURL);
    }
    reset();
  };

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    reset
  } = useCombobox({
    onInputValueChange: async ({ inputValue }) => {
      setInputValue(inputValue || '');
      if (!inputValue || inputValue.length < 3) {
        setItems([]);
        return;
      }
      try {
        const { data: items } = await globalSearch(inputValue);
        setItems(items || []);
      } catch (error) {
        console.error('Search error:', error);
        setItems([]);
      }
    },
    items,
    itemToString(item) {
      return item ? item.title : '';
    },
    onSelectedItemChange: handleSelectedItemChange
  });

  return (
    <div className={clsx('relative lg:w-[400px]', className)}>
      <div className="relative w-full">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <MagnifyingGlass size={20} className="text-gray-500" />
        </div>
        <input
          style={{ borderRadius: styleRadius }}
          className="block w-full border-none pl-10 pr-10 py-2 text-gray-900 text-md placeholder-gray-600 bg-[#f6f6f6] h-12 focus:ring-2 focus:ring-gray-200 transition-all"
          placeholder="Search profiles and groups..."
          {...getInputProps()}
        />
        {inputValue && (
          <button
            onClick={() => {
              reset();
              setInputValue('');
              setItems([]);
            }}
            className="absolute inset-y-0 right-3 flex items-center">
            <span className="p-1 hover:bg-gray-200 rounded-full transition-colors">
              <X size={16} weight="bold" className="text-gray-500" />
            </span>
          </button>
        )}
      </div>

      <ul
        className={`absolute w-full bg-white mt-2 shadow-xl max-h-[420px] overflow-y-auto z-10 divide-y divide-gray-100 border border-gray-100 rounded-xl ${
          !(isOpen && items.length) && 'hidden'
        }`}
        {...getMenuProps()}>
        {isOpen &&
          items.map((item, index) => (
            <li
              className={clsx(
                highlightedIndex === index && 'bg-gray-50',
                selectedItem === item && 'bg-gray-50',
                'cursor-pointer hover:bg-gray-50 transition-colors'
              )}
              key={item.id}
              {...getItemProps({ item, index })}>
              <div className="px-4 py-3">
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center"
                    style={{ borderRadius: styleRadius, background: '#f3f4f6' }}>
                    {item.type === 'profile' ? (
                      <svg
                        className="w-4 h-4 text-gray-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4 text-gray-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="truncate">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">
                            <HighlightedText text={item.name || '-'} highlight={inputValue} />
                          </h4>
                          {item.type === 'profile' && item.isApproved && (
                            <CheckCircle size={16} className="text-green-500" weight="fill" />
                          )}
                        </div>
                        {item.groupName && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            <HighlightedText text={item.groupName} highlight={inputValue} />
                          </p>
                        )}
                      </div>
                      <span
                        style={{ borderRadius: styleRadius }}
                        className={clsx(
                          'text-xs uppercase px-2.5 py-1 font-medium ml-2 flex-shrink-0',
                          {
                            'bg-blue-50 text-blue-700 border border-blue-100':
                              item.type === 'profile',
                            'bg-purple-50 text-purple-700 border border-purple-100':
                              item.type === 'group'
                          }
                        )}>
                        {item.type}
                      </span>
                    </div>
                    {item.email && (
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="truncate">
                          <HighlightedText text={item.email} highlight={inputValue} />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
      </ul>
    </div>
  );
}
